import { useEffect, useRef, useState } from 'react'
import { Col, Form, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import ReactSelect, { createFilter } from 'react-select'

import { AttachmentView } from '../../../common/attachments/attachmentview'
import { Button } from '../../../common/Button.tsx'
import { IconAddNew } from '../../../common/icons'
import { PersonSelect } from '../../../common/personselect'
import { reactSelectSearchSettings } from '../../../common/reactselect'
import { actionTypeSelector, authSelector } from '../../../reducers'

export function AddLanguage({ show, setShow, languageOptions, languageData, saveData, person }) {
	const { t } = useTranslation()
	const auth = useSelector(authSelector)
	const actionType = useSelector(actionTypeSelector)

	const path = window.location.pathname
	const isPersonal = path.startsWith('/personal')

	const languageCodeRef = useRef(null)
	const languageSkillLevelRef = useRef(null)
	const commentRef = useRef(null)

	const [data, setData] = useState(null)

	useEffect(() => setData(languageData), [languageData])

	const dataChange = ({ target }) => {
		const value = target.type === 'checkbox' ? target.checked : target.value
		setData({ ...data, [target.id]: value })
		switch (target.id) {
			case 'languageSkillLevel':
				commentRef.current.focus()
				break
		}
	}

	// react-select
	const selectChange = (newValue, actionMeta) => {
		setData({ ...data, [actionMeta.name]: newValue.value })
		switch (actionMeta.name) {
			case 'personId':
				languageCodeRef.current.focus()
				break
			case 'languageCode':
				languageSkillLevelRef.current.focus()
				break
		}
	}

	const handleSave = () => saveData(data)

	const handleClose = () => setShow(false)
	const handleShow = () => {
		let initialData = { languageId: 0 }

		if (person?.personId && isPersonal) {
			initialData = {
				...initialData,
				personId: person?.personId,
				personNumber: person?.nr,
				personName: person?.name,
			}
		}

		setData(initialData)
		setShow(true)
	}

	return (
		<>
			<Button testId="add-skill" variant="primary" className="competencebtn addbutton" onClick={handleShow}>
				<IconAddNew /> {t('AddLanguageSkill', 'Lisää kielitaito')}
			</Button>

			{show && (
				<Modal show onHide={handleClose} backdrop="static" size="xl" fullscreen="lg-down" scrollable>
					<Modal.Header closeButton>
						<Modal.Title>
							{data?.languageId > 0 ? t('LanguageSkill', 'Kielitaito') : t('AddLanguageSkill', 'Lisää kielitaito')}
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<Row className="mb-1">
							<PersonSelect
								disabled={data?.languageId > 0 || isPersonal}
								selectedPerson={data}
								selectChange={selectChange}
								supervised={auth.role === 'SUPERVISOR'}
							/>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} controlId="languageCode">
								<Form.Label>{t('Language', 'Kieli')}</Form.Label>
								<ReactSelect
									name="languageCode"
									inputId="languageCode"
									options={languageOptions}
									value={languageOptions.find((option) => option.value === data?.languageCode)}
									onChange={selectChange}
									ref={languageCodeRef}
									{...reactSelectSearchSettings(t)}
									filterOption={createFilter({
										matchFrom: 'start',
										//stringify: option => `${option.label.slice(5)} (${option.value})`,
									})}
								/>
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} controlId="languageSkillLevel">
								<Form.Label>{t('Level', 'Taso')}</Form.Label>
								<Form.Select onChange={dataChange} value={data?.languageSkillLevel} ref={languageSkillLevelRef}>
									<option>{t('Select', 'Valitse')}</option>
									<option value="A1">{t('LanguageSkillLevel_A1', 'A1 - kielen alkeet')}</option>
									<option value="A2">{t('LanguageSkillLevel_A2', 'A2 - kehittyvä peruskielitaito')}</option>
									<option value="B1">
										{t('LanguageSkillLevel_B1', 'B1 - melko sujuva ja toimiva peruskielitaito')}
									</option>
									<option value="B2">{t('LanguageSkillLevel_B2', 'B2 - itsenäinen ja toimiva kielitaito')}</option>
									<option value="C1">{t('LanguageSkillLevel_C1', 'C1 - taitavan kielitaidon perustaso')}</option>
									<option value="C2">{t('LanguageSkillLevel_C2', 'C2 - taitava tai äidinkielen taso')}</option>
									<option value="NATIVE">{t('NativeLanguage', 'Äidinkieli')}</option>
								</Form.Select>
							</Form.Group>
						</Row>

						<Row className="mb-1">
							<Form.Group as={Col} controlId="comment">
								<Form.Label>{t('Comment', 'Kommentti')}</Form.Label>
								<Form.Control
									as="textarea"
									required
									rows={5}
									onChange={dataChange}
									value={data?.comment || ''}
									ref={commentRef}
								/>
							</Form.Group>
						</Row>

						<h4>{t('Attachments', 'Liitteet')}</h4>
						<AttachmentView
							section="languageSkill"
							entityid={data?.languageId}
							attachments={data?.attachments}
							savedId={
								(actionType === 'POST_LANGUAGE_SUCCEEDED' || actionType === 'POST_LANGUAGE_EDIT_SUCCEEDED') &&
								data?.languageId
							}
						/>
					</Modal.Body>
					<Modal.Footer style={{ display: 'flex', justifyContent: 'space-between' }}>
						<Button variant="cancel" onClick={handleClose}>
							{t('Cancel', 'Keskeytä')}
						</Button>
						<Button variant="primary" onClick={handleSave} disabled={!data?.personId}>
							{t('Save', 'Tallenna')}
						</Button>
					</Modal.Footer>
				</Modal>
			)}
		</>
	)
}
